// src/pages/BucketListPage.js
import React,{useEffect, useState} from 'react';
import { api } from '../config';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './BucketListPage.css';


function isTokenExpired(token) {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload.exp) return true;
    return Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
}

const BucketListPage = () => {
  const navigate = useNavigate();
  const [checklists, setChecklists] = React.useState([]);
  const [warningMessage] = React.useState('');
  const location = useLocation();
  const { isLoggedIn, accessToken } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  
  React.useEffect(() => {
    const fetchBucketLists = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(api.url('/api/bucketlist/all'), {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
      
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Error fetching lists: ${errorText}`);
          }
      
          const data = await response.json();
          const parsed = data.map(list => ({
            ...list,
            isSaved: true, // ✅ ADD THIS LINE — all fetched lists are saved
            items: Array.isArray(list.items)
              ? list.items.map(i => (typeof i === 'string' ? JSON.parse(i) : i))
              : []
          }));
          
          
          setChecklists(parsed || []);

        } catch  {
          
          setChecklists([]); // fallback to empty to avoid .map error
        }
      };
      
    fetchBucketLists();
  }, []);
  useEffect(() => {
    if (!isLoggedIn || isTokenExpired(accessToken)) {
      navigate('/login', { state: { from: location.pathname } });
    }
  }, [isLoggedIn, accessToken, navigate, location.pathname]);
  
  const handleSaveNewBucketList = async (list) => {
    const token = localStorage.getItem('token');
    if (!token) return alert('You must be logged in.');
    const filteredItems = list.items.filter((item) => {
      return (
        item.activity.trim() !== '' ||
        item.location.trim() !== '' ||
        (item.target_date ?? '').trim() !== ''
      );
    });
    
    if (filteredItems.length === 0) {
      alert('Please add at least one item before saving.');
      return;
    }
    try {
      const res = await fetch(api.url('/api/bucketlist/create'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: list.title,
          items: filteredItems,
        }),
      });
  
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to create bucket list');
  
      // ✅ Fix here: Replace temp checklist with new one
      setChecklists(prev => {
        // Remove any existing checklist with same title and same items (unsaved temp copy)
        const filtered = prev.filter(c => {
          const isSameTitle = c.title === list.title;
          const isSameItems = JSON.stringify(c.items) === JSON.stringify(list.items);
          return !(isSameTitle && isSameItems);
        });
      
        return [
          ...filtered,
          {
            id: data.bucketListId,
            title: list.title,
            items: filteredItems,
            isSaved: true,
          }
        ];
      });
      
    } catch  {
      
      alert('Something went wrong while saving.');
    }
  };
  
  
  const handleToggleItemCheck = async (itemId, newChecked) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(api.url(`/api/bucketlist/item/${itemId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ is_checked: newChecked })
      });
  
      // Update local state
      setChecklists((prev) =>
        prev.map((list) => ({
          ...list,
          items: list.items.map((item) =>
            item.id === itemId ? { ...item, is_checked: newChecked } : item
          )
        }))
      );
    } catch  {
      
    }
  };
  const handleDeleteChecklist = async (id) => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('You must be logged in.');
      
      return;
    }
  
    const confirmDelete = window.confirm('Are you sure you want to delete this bucket list?');
    if (!confirmDelete) return;
  
    const isTemp = typeof id !== 'number';
    
  
    // Always remove from UI
    setChecklists(prev => {
      
      return prev.filter(c => c.id !== id);
    });
  
    if (!isTemp) {
      try {
        const res = await fetch(api.url(`/api/bucketlist/${id}`), {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
  
        await res.text();
        
  
        if (!res.ok) { 
        } 
      } catch  {
       
        alert('Something went wrong. Please try again.');
      }
    } 
  };
  
  const handleChecklistTitleChange = (listIndex, value) => {
    setChecklists(prev => {
      const newLists = [...prev];
      newLists[listIndex].title = value;
      return newLists;
    });
  };
  
  const handleChecklistItemChange = (listIndex, itemIndex, field, value) => {
    setChecklists(prev => {
      const newLists = [...prev];
      if (!newLists[listIndex].items[itemIndex]) return newLists;
      newLists[listIndex].items[itemIndex][field] = value;
      return newLists;
    });
  };
  const handleRemoveChecklistItem = (listIndex, itemIndex) => {
    setChecklists(prev => {
      const newLists = [...prev];
      const updatedItems = [...newLists[listIndex].items];
      updatedItems.splice(itemIndex, 1); // remove specific row
      newLists[listIndex].items = updatedItems;
      return newLists;
    });
  };
  
  const handleAddChecklistItem = (listIndex) => {
    setChecklists((prev) => {
      const newLists = [...prev];
  
      if (!Array.isArray(newLists[listIndex].items)) {
        newLists[listIndex].items = [];
      }
  
      newLists[listIndex].items.push({
        activity: '',
        location: '',
        target_date: '',
        is_checked: false
      });
  
      return newLists;
    });
  };
   ;
  
  
  return (
    <div className="bucket-list-page">
      {/* Header */}
      <div className="header">
            <div className="brand" onClick={() => navigate("/")}>
            <img src="/images/customroam-logo.png" alt="Logo" className="logo-img" />
            <span className="brand-text">CUSTOM ROAM</span>
             </div>
              {/* Hamburger menu button */}
              <button className="header-menu-btn" onClick={() => setMenuOpen(!menuOpen)}>
                ☰
               </button>
             <div className={`nav-links ${menuOpen ? 'open' : ''}`}>
                    <span className="nav-link" onClick={() => navigate('/')}>Home</span>
                    <span className="nav-link" onClick={() => navigate('/roam-share')}>Roam Share</span>
                    <span className="nav-link" onClick={() => navigate('/my-itineraries')}>My Itineraries</span>
                    <span className="notification-icon" onClick={() => navigate('/notifications')}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                       height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                     className="bell-svg"
                    >
                        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"></path>
                         <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                    </svg>
                 </span>
                    <span className="nav-link" onClick={() => navigate('/profile')}>Profile</span>
                </div>
            </div>

      {/* Heading + Create Bucket List */}
      <div className="bucket-list-content">
      <h2>My Bucket Lists</h2>

      <div className="bucket-lists-section">
      {checklists.map((list, index) => (
  <div className="checklist-box" key={list.id || `temp-${index}`}>
    <button
      className="delete-checklist-button"
      onClick={() => handleDeleteChecklist(list.id)}
      title="Delete this bucket list"
    >
      ✕
    </button>

    {/* Title input */}
    {list.isSaved ? (
    <h4 className="checklist-title-label">{list.title}</h4>
      ) : (
     <input
     type="text"
     value={list.title}
     onChange={(e) => handleChecklistTitleChange(index, e.target.value)}
     placeholder="Enter bucket list title"
     className="checklist-title-input"
     />
     )}
 

    {/* Destination rows */}
    {(list.items || []).map((item, itemIdx) => (
      <div className="checklist-item-row" key={item.id || `item-${index}-${itemIdx}`}>
        <input
          type="checkbox"
          checked={item.is_checked || false}
          onChange={(e) => {
            if (!item.id) return; // Skip toggle if ID is undefined (unsaved item)
            handleToggleItemCheck(item.id, e.target.checked);
          }}
          
        />
        {list.isSaved ? (
  <div
    className="destination-sentence"
    style={{
      textDecoration: item.is_checked ? 'line-through' : 'none',
      color: item.is_checked ? '#888' : 'inherit'
    }}
  >
    {item.activity || '—'} at {item.location || '—'} by {(item.target_date || '—').split('T')[0]}
  </div>
) : (

  <div className="checklist-item-group">
    <input
      type="text"
      value={item.activity || ''}
      onChange={(e) => handleChecklistItemChange(index, itemIdx, 'activity', e.target.value)}
      placeholder="Activity or Destination"
      className="activity-input"
    />
    <input
      type="text"
      value={item.location || ''}
      onChange={(e) => handleChecklistItemChange(index, itemIdx, 'location', e.target.value)}
      placeholder="City or Country"
      className="location-input"
    />
    <input
     type="date"
     value={item.target_date || ''}
     onChange={(e) => handleChecklistItemChange(index, itemIdx, 'target_date', e.target.value)}
     className="date-input"
     min={new Date().toISOString().split("T")[0]}  // ✅ restrict past dates
     />
    {/* ❌ Remove Row Button */}
  <button
    className="remove-item-button"
    onClick={() => handleRemoveChecklistItem(index, itemIdx)}
    title="Remove this row"
  >
    ×
  </button>
  </div>
)}
      </div>
    ))}

    {/* + Add More and Save buttons only if NOT saved */}
    {!list.isSaved && (
  <>
    <p
      className="add-more-link"
      onClick={() => handleAddChecklistItem(index)}
    >
      + Add more
    </p>

    {/* ✅ Show warning message below "+ Add more" if not filled */}
    {warningMessage && (
      <p className="checklist-warning">{warningMessage}</p>
    )}

    <div className="checklist-actions">
      <button
        className="save-list-button"
        onClick={() => handleSaveNewBucketList(list)}
      >
        Save Bucket List
      </button>
    </div>
  </>
)}

  </div>
))}
       <div
       className="create-bucket-wrapper"
       onClick={() =>
        setChecklists([
          ...checklists,
          {
            id: Date.now(), // temporary ID
            title: '',
            items: [{ activity: '', location: '', target_date: '', is_checked: false }],
            isSaved: false
          }
        ])
      }
      
      style={{ marginLeft: '20px' }}
      >

     <div className="plus-circle">+</div>
     <div className="create-text">Create Bucket List</div>
     </div>
     </div>

      </div>

    </div>
  );
};

export default BucketListPage;
